export const SHELL_THEME_STORAGE_KEY = "sidebarShellTheme";
export const SHELL_THEME_CHANGED_EVENT = "sidebar-shell-theme-changed";
/** Default shell when `sidebarShellTheme` is not set in localStorage. */
export const SHELL_THEME_DEFAULT_VARIANT = "2";

/** Theme 2 — Sidebar Appearance accent palette, first swatch (sidebar background). */
export const THEME2_ACCENT_PRESET_FIRST = "#212529";

export const THEME2_SIDEBAR_BG_STORAGE_KEY = "sidebarShellTheme2Background";
export const THEME2_SIDEBAR_BG_CHANGED_EVENT = "sidebar-shell-theme2-background-changed";

const SIDEBAR_CUSTOM_COLOR_STORAGE_KEY = "sidebarCustomColor";

const CSS_VAR_SIDEBAR_BG = "--sidebar-shell-theme-2-bg";

function normalizeSidebarBgHex(hex, fallback = THEME2_ACCENT_PRESET_FIRST) {
  const trimmed = String(hex ?? "").trim();
  if (!trimmed) {
    return normalizeSidebarBgHex(fallback, THEME2_ACCENT_PRESET_FIRST);
  }
  let value = trimmed.startsWith("#") ? trimmed.slice(1) : trimmed;
  if (/^[0-9A-Fa-f]{3}$/.test(value)) {
    value = value
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (/^[0-9A-Fa-f]{6}$/.test(value)) {
    return `#${value.toUpperCase()}`;
  }
  return normalizeSidebarBgHex(fallback, THEME2_ACCENT_PRESET_FIRST);
}

/** Theme 2 sidebar menu labels (`p` / `.sidebar-normal`) — set from Sidebar Appearance. */
export const THEME2_SIDEBAR_NAV_FONT_SIZE_STORAGE_KEY = "sidebarShellTheme2NavFontSizePx";
export const THEME2_SIDEBAR_NAV_LINE_HEIGHT_STORAGE_KEY = "sidebarShellTheme2NavLineHeight";
export const THEME2_SIDEBAR_NAV_TYPOGRAPHY_CHANGED_EVENT = "sidebar-shell-theme2-nav-typography-changed";

export const THEME2_SIDEBAR_NAV_FONT_SIZE_MIN_PX = 12;
export const THEME2_SIDEBAR_NAV_FONT_SIZE_MAX_PX = 16;
export const THEME2_SIDEBAR_NAV_LINE_HEIGHT_MIN = 1.4;
export const THEME2_SIDEBAR_NAV_LINE_HEIGHT_MAX = 2;

/** Theme 2 sidebar nav line-height — Sidebar Appearance (S / M / L / XL). */
export const THEME2_SIDEBAR_NAV_LINE_HEIGHT_SMALL = 1.4;
export const THEME2_SIDEBAR_NAV_LINE_HEIGHT_MEDIUM = 1.65;
export const THEME2_SIDEBAR_NAV_LINE_HEIGHT_LARGE = 1.9;
export const THEME2_SIDEBAR_NAV_LINE_HEIGHT_XLARGE = 2;

/** Theme 2 sidebar nav font — Sidebar Appearance (S / M / L / XL). */
export const THEME2_SIDEBAR_NAV_FONT_SIZE_SMALL_PX = 12;
export const THEME2_SIDEBAR_NAV_FONT_SIZE_MEDIUM_PX = 14;
export const THEME2_SIDEBAR_NAV_FONT_SIZE_LARGE_PX = 15;
export const THEME2_SIDEBAR_NAV_FONT_SIZE_XLARGE_PX = 16;

/** When nothing is stored — same as Medium presets (Sidebar Appearance defaults). */
export const THEME2_SIDEBAR_NAV_FONT_SIZE_DEFAULT_PX = THEME2_SIDEBAR_NAV_FONT_SIZE_MEDIUM_PX;
export const THEME2_SIDEBAR_NAV_LINE_HEIGHT_DEFAULT = THEME2_SIDEBAR_NAV_LINE_HEIGHT_MEDIUM;

export const THEME2_SIDEBAR_NAV_FONT_SIZE_CSS_VAR = "--sidebar-shell-theme-2-nav-font-size";
export const THEME2_SIDEBAR_NAV_FONT_SIZE_VAR = `var(${THEME2_SIDEBAR_NAV_FONT_SIZE_CSS_VAR}, 14px)`;
const CSS_VAR_NAV_LINE_HEIGHT = "--sidebar-shell-theme-2-nav-line-height";

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

const THEME2_FONT_PRESET_ORDER = [
  THEME2_SIDEBAR_NAV_FONT_SIZE_SMALL_PX,
  THEME2_SIDEBAR_NAV_FONT_SIZE_MEDIUM_PX,
  THEME2_SIDEBAR_NAV_FONT_SIZE_LARGE_PX,
  THEME2_SIDEBAR_NAV_FONT_SIZE_XLARGE_PX,
];

/** Maps any stored px (e.g. legacy slider) to the nearest S/M/L preset for UI selection. */
export function snapTheme2SidebarNavFontSizePxToNearestPreset(px) {
  const v = clamp(
    Math.round(Number(px)),
    THEME2_SIDEBAR_NAV_FONT_SIZE_MIN_PX,
    THEME2_SIDEBAR_NAV_FONT_SIZE_MAX_PX
  );
  let best = THEME2_SIDEBAR_NAV_FONT_SIZE_MEDIUM_PX;
  let bestD = Infinity;
  for (const n of THEME2_FONT_PRESET_ORDER) {
    const d = Math.abs(n - v);
    if (d < bestD || (d === bestD && n < best)) {
      best = n;
      bestD = d;
    }
  }
  return best;
}

const THEME2_LINE_PRESET_ORDER = [
  THEME2_SIDEBAR_NAV_LINE_HEIGHT_SMALL,
  THEME2_SIDEBAR_NAV_LINE_HEIGHT_MEDIUM,
  THEME2_SIDEBAR_NAV_LINE_HEIGHT_LARGE,
  THEME2_SIDEBAR_NAV_LINE_HEIGHT_XLARGE,
];

/** Maps any stored line-height (e.g. legacy slider) to the nearest S/M/L preset for UI selection. */
export function snapTheme2SidebarNavLineHeightToNearestPreset(lh) {
  const v = clamp(
    Math.round(Number(lh) * 100) / 100,
    THEME2_SIDEBAR_NAV_LINE_HEIGHT_MIN,
    THEME2_SIDEBAR_NAV_LINE_HEIGHT_MAX
  );
  let best = THEME2_SIDEBAR_NAV_LINE_HEIGHT_MEDIUM;
  let bestD = Infinity;
  for (const n of THEME2_LINE_PRESET_ORDER) {
    const d = Math.abs(n - v);
    if (d < bestD || (d === bestD && n < best)) {
      best = n;
      bestD = d;
    }
  }
  return best;
}

export function getTheme2SidebarNavFontSizePxFromStorage() {
  if (typeof window === "undefined") return THEME2_SIDEBAR_NAV_FONT_SIZE_DEFAULT_PX;
  const raw = localStorage.getItem(THEME2_SIDEBAR_NAV_FONT_SIZE_STORAGE_KEY);
  const n = raw == null || raw === "" ? NaN : Number(raw);
  if (!Number.isFinite(n)) return THEME2_SIDEBAR_NAV_FONT_SIZE_DEFAULT_PX;
  return Math.round(clamp(n, THEME2_SIDEBAR_NAV_FONT_SIZE_MIN_PX, THEME2_SIDEBAR_NAV_FONT_SIZE_MAX_PX));
}

export function getTheme2SidebarNavLineHeightFromStorage() {
  if (typeof window === "undefined") return THEME2_SIDEBAR_NAV_LINE_HEIGHT_DEFAULT;
  const raw = localStorage.getItem(THEME2_SIDEBAR_NAV_LINE_HEIGHT_STORAGE_KEY);
  const n = raw == null || raw === "" ? NaN : Number(raw);
  if (!Number.isFinite(n)) return THEME2_SIDEBAR_NAV_LINE_HEIGHT_DEFAULT;
  const rounded = Math.round(n * 100) / 100;
  return clamp(rounded, THEME2_SIDEBAR_NAV_LINE_HEIGHT_MIN, THEME2_SIDEBAR_NAV_LINE_HEIGHT_MAX);
}

/** Pushes stored Theme 2 nav typography to `document.documentElement` CSS variables. */
export function applyTheme2SidebarNavTypographyToDocument() {
  if (typeof document === "undefined") return;
  const px = getTheme2SidebarNavFontSizePxFromStorage();
  const lh = getTheme2SidebarNavLineHeightFromStorage();
  document.documentElement.style.setProperty(THEME2_SIDEBAR_NAV_FONT_SIZE_CSS_VAR, `${px}px`);
  document.documentElement.style.setProperty(CSS_VAR_NAV_LINE_HEIGHT, String(lh));
}

export function setTheme2SidebarNavTypographyInStorage(fontSizePx, lineHeight) {
  if (typeof window === "undefined") return;
  const px = Math.round(
    clamp(Number(fontSizePx), THEME2_SIDEBAR_NAV_FONT_SIZE_MIN_PX, THEME2_SIDEBAR_NAV_FONT_SIZE_MAX_PX)
  );
  const lh = clamp(
    Math.round(Number(lineHeight) * 100) / 100,
    THEME2_SIDEBAR_NAV_LINE_HEIGHT_MIN,
    THEME2_SIDEBAR_NAV_LINE_HEIGHT_MAX
  );
  localStorage.setItem(THEME2_SIDEBAR_NAV_FONT_SIZE_STORAGE_KEY, String(px));
  localStorage.setItem(THEME2_SIDEBAR_NAV_LINE_HEIGHT_STORAGE_KEY, String(lh));
  applyTheme2SidebarNavTypographyToDocument();
  window.dispatchEvent(new Event(THEME2_SIDEBAR_NAV_TYPOGRAPHY_CHANGED_EVENT));
}

/** Resolved Theme 2 sidebar background (dedicated key → accent preset → default). */
export function getTheme2SidebarBackgroundColor() {
  if (typeof window === "undefined") return THEME2_ACCENT_PRESET_FIRST;

  const dedicated = localStorage.getItem(THEME2_SIDEBAR_BG_STORAGE_KEY);
  if (dedicated) {
    return normalizeSidebarBgHex(dedicated, THEME2_ACCENT_PRESET_FIRST);
  }

  const accent = localStorage.getItem(SIDEBAR_CUSTOM_COLOR_STORAGE_KEY);
  if (accent) {
    return normalizeSidebarBgHex(accent, THEME2_ACCENT_PRESET_FIRST);
  }

  return THEME2_ACCENT_PRESET_FIRST;
}

/** Persists sidebar bg and applies `--sidebar-shell-theme-2-bg` when shell theme 2 is active. */
export function setTheme2SidebarBackgroundInStorage(hex) {
  if (typeof window === "undefined") return;
  const normalized = normalizeSidebarBgHex(hex, THEME2_ACCENT_PRESET_FIRST);
  localStorage.setItem(THEME2_SIDEBAR_BG_STORAGE_KEY, normalized);
  applyTheme2SidebarBackgroundToDocument();
  window.dispatchEvent(new Event(THEME2_SIDEBAR_BG_CHANGED_EVENT));
}

/** Sets `--sidebar-shell-theme-2-bg` when shell theme 2 is active. */
export function applyTheme2SidebarBackgroundToDocument() {
  if (typeof document === "undefined") return;
  if (getShellThemeFromStorage() === "2") {
    document.documentElement.style.setProperty(
      CSS_VAR_SIDEBAR_BG,
      getTheme2SidebarBackgroundColor()
    );
  } else {
    document.documentElement.style.removeProperty(CSS_VAR_SIDEBAR_BG);
  }
}

export function getShellThemeFromStorage() {
  if (typeof window === "undefined") return SHELL_THEME_DEFAULT_VARIANT;
  const v = localStorage.getItem(SHELL_THEME_STORAGE_KEY);
  if (v === "1") return "1";
  return SHELL_THEME_DEFAULT_VARIANT;
}

export function setShellThemeInStorage(variant) {
  const v = variant === "2" ? "2" : "1";
  localStorage.setItem(SHELL_THEME_STORAGE_KEY, v);
  if (v === "2") {
    if (!localStorage.getItem(THEME2_SIDEBAR_BG_STORAGE_KEY)) {
      const accent = localStorage.getItem(SIDEBAR_CUSTOM_COLOR_STORAGE_KEY);
      setTheme2SidebarBackgroundInStorage(accent || THEME2_ACCENT_PRESET_FIRST);
    } else {
      applyTheme2SidebarBackgroundToDocument();
    }
  } else {
    applyTheme2SidebarBackgroundToDocument();
  }
  window.dispatchEvent(new Event(SHELL_THEME_CHANGED_EVENT));
}
